// ui/src/components/tenant-admin/sections/PlanIdentitySection.jsx

/**
 * @file PlanIdentitySection.jsx
 * @description Plan document section for core identity fields.
 *
 * S810 REWRITE — OI-S810-SECTION-FORM-STATE
 *   - Owns local form state via useState (no onFieldChange to parent during editing).
 *   - Exposes getValues() via useImperativeHandle so PlanDocumentCanvas.handleSave()
 *     can collect form state in one call → one PATCH.
 *   - useEffect resets form only when plan.planId changes (not every render).
 *   - defaultProps REMOVED (React 18 deprecated) — default in function signature.
 *
 * @module tenant-admin/sections
 * @author data-fit LLC · DecisionFlow · S775 / S810
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import TemporalRelationshipBadge from '../../shared/TemporalRelationshipBadge.jsx';

// ---------------------------------------------------------------------------
// initFromPlan — builds the section's form shape from the plan prop
// ---------------------------------------------------------------------------
function initFromPlan(plan) {
  return {
    planName:       plan.planName       ?? '',
    roleId:         plan.roleId         ?? null,
    segmentId:      plan.segmentId      ?? null,
    businessUnitId: plan.businessUnitId ?? null,
    fiscalPeriodId: plan.fiscalPeriodId ?? null,
    effectiveFrom:  plan.effectiveFrom  ?? '',
    effectiveTo:    plan.effectiveTo    ?? '',
  };
}

const PlanIdentitySection = forwardRef(function PlanIdentitySection({
  plan,
  roles,
  segments,
  businessUnits = [],
  fiscalPeriods,
  readOnly,
  validationErrors = {},
}, ref) {
  // Section-local form state — initialized from plan prop, updated locally.
  // No network calls here. No callbacks to parent during editing.
  const [form, setForm] = useState(() => initFromPlan(plan));

  // Reset form only when the plan changes (different planId), not on every render.
  useEffect(() => {
    setForm(initFromPlan(plan));
  }, [plan.planId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Expose getValues() so PlanDocumentCanvas.handleSave() can collect this
  // section's current form state and send it in one PATCH.
  useImperativeHandle(ref, () => ({
    getValues() {
      // Returns the fields this section owns in PlanPatchBody camelCase shape.
      return {
        planName:       form.planName,
        roleId:         form.roleId,
        segmentId:      form.segmentId,
        businessUnitId: form.businessUnitId,
        fiscalPeriodId: form.fiscalPeriodId,
        effectiveFrom:  form.effectiveFrom || null,
        effectiveTo:    form.effectiveTo   || null,
      };
    },
  }), [form]);

  // ---------------------------------------------------------------------------
  // Local change handlers — update form state only, no network
  // ---------------------------------------------------------------------------
  const handleTextChange = (field) => (event) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleNumericSelectChange = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value === '' ? null : Number(value) }));
  };

  // ---------------------------------------------------------------------------
  // Helpers (unchanged from S807)
  // ---------------------------------------------------------------------------
  const planNameLength      = form.planName?.length ?? 0;
  const showPlanNameCounter = !readOnly && planNameLength >= 80;
  const fieldError  = (field) => validationErrors?.[field] || null;
  const errorId     = (field) => `plan-identity-${field}-error`;

  const describedBy = (field, additionalId = null) => {
    const ids = [];
    if (additionalId)    ids.push(additionalId);
    if (fieldError(field)) ids.push(errorId(field));
    return ids.length > 0 ? ids.join(' ') : undefined;
  };

  const formatDate = (value) => {
    if (!value) return 'Open-ended';
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) return value;
    return new Intl.DateTimeFormat(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(new Date(Date.UTC(year, month - 1, day)));
  };

  const displayValue = (value, fallback = 'Not specified') =>
    value === null || value === undefined || value === '' ? fallback : value;

  // Derived display values for read-only mode
  const roleName    = roles.find((r) => r.roleId    === form.roleId)?.roleName    ?? '';
  const segmentName = segments.find((s) => s.segmentId === form.segmentId)?.segmentName ?? '';
  const buName      = businessUnits.find((b) => b.businessUnitId === form.businessUnitId)?.businessUnitName ?? '';
  const fpLabel     = fiscalPeriods.find((p) => p.fiscalPeriodId === form.fiscalPeriodId)?.periodLabel ?? '';

  return (
    <section
      className="modifier-manager-card plan-identity-section"
      aria-labelledby="plan-identity-heading"
    >
      <style>{`
        .plan-identity-section {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          color: rgb(var(--color-text));
          font-family: "IBM Plex Sans", sans-serif;
        }
        .plan-identity-section .modifier-manager-header {
          align-items: flex-start;
          border-bottom: 1px solid rgb(var(--color-border));
          display: flex;
          gap: 16px;
          justify-content: space-between;
          padding: 16px;
        }
        .plan-identity-section .modifier-manager-body {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 16px;
        }
        .plan-identity-heading-group { min-width: 0; }
        .plan-identity-heading {
          color: rgb(var(--color-text));
          font-size: 18px;
          font-weight: 600;
          line-height: 1.35;
          margin: 0;
        }
        .plan-identity-version {
          color: rgb(var(--color-text-muted));
          font-size: 13px;
          line-height: 1.4;
          margin: 4px 0 0;
        }
        .plan-identity-status {
          color: rgb(var(--color-text-muted));
          flex: 0 0 auto;
          font-size: 12px;
          font-weight: 500;
          line-height: 1.4;
          margin: 2px 0 0;
        }
        .plan-identity-status-label { font-weight: 400; }
        .plan-identity-grid {
          display: grid;
          gap: 16px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }
        .plan-identity-field { min-width: 0; }
        .plan-identity-field--full { grid-column: 1 / -1; }
        .plan-identity-label {
          color: rgb(var(--color-text));
          display: block;
          font-size: 13px;
          font-weight: 600;
          line-height: 1.4;
          margin-bottom: 6px;
        }
        .plan-identity-required {
          color: rgb(var(--color-text-muted));
          font-weight: 400;
        }
        .plan-identity-input,
        .plan-identity-select {
          background: rgb(var(--color-surface));
          border: 1px solid rgb(var(--color-border));
          border-radius: 6px;
          box-sizing: border-box;
          color: rgb(var(--color-text));
          font: inherit;
          font-size: 14px;
          line-height: 1.4;
          min-height: 40px;
          padding: 8px 10px;
          width: 100%;
        }
        .plan-identity-input::placeholder { color: rgb(var(--color-placeholder)); }
        .plan-identity-input[aria-invalid="true"],
        .plan-identity-select[aria-invalid="true"] { border-color: rgb(var(--color-danger)); }
        .plan-identity-field-footer {
          align-items: flex-start;
          display: flex;
          gap: 12px;
          justify-content: space-between;
        }
        .plan-identity-error {
          color: rgb(var(--color-danger));
          font-size: 12px;
          margin: 5px 0 0;
        }
        .plan-identity-counter {
          color: rgb(var(--color-text-muted));
          font-size: 12px;
          flex-shrink: 0;
          margin: 5px 0 0;
        }
        .plan-identity-readonly-value {
          font-size: 14px;
          line-height: 1.4;
          padding: 8px 0;
        }
        .plan-identity-readonly-muted { color: rgb(var(--color-text-muted)); }
      `}</style>

      <header className="modifier-manager-header">
        <div className="plan-identity-heading-group">
          <h2 id="plan-identity-heading" className="plan-identity-heading">
            Plan Identity
          </h2>
          <p className="plan-identity-version">
            v{plan.version ?? 1}
          </p>
        </div>
        <div className="plan-identity-status">
          <span className="plan-identity-status-label">Status: </span>
          {plan.planStatus ?? 'draft'}
          {plan.temporalRelationship && (
            <TemporalRelationshipBadge relationship={plan.temporalRelationship} />
          )}
        </div>
      </header>

      <div className="modifier-manager-body">
        {readOnly ? (
          // ── Read-only display ────────────────────────────────────────────
          <dl className="plan-identity-grid">
            <div className="plan-identity-field plan-identity-field--full">
              <dt className="plan-identity-label">Plan name</dt>
              <dd className="plan-identity-readonly-value">
                {displayValue(form.planName)}
              </dd>
            </div>
            <div className="plan-identity-field">
              <dt className="plan-identity-label">Role</dt>
              <dd className="plan-identity-readonly-value">
                {displayValue(roleName)}
              </dd>
            </div>
            <div className="plan-identity-field">
              <dt className="plan-identity-label">Segment</dt>
              <dd className="plan-identity-readonly-value">
                {displayValue(segmentName)}
              </dd>
            </div>
            <div className="plan-identity-field plan-identity-field--full">
              <dt className="plan-identity-label">Fiscal period</dt>
              <dd className="plan-identity-readonly-value">
                {displayValue(fpLabel)}
              </dd>
            </div>
            <div className="plan-identity-field">
              <dt className="plan-identity-label">Effective from</dt>
              <dd className="plan-identity-readonly-value">
                {formatDate(form.effectiveFrom)}
              </dd>
            </div>
            <div className="plan-identity-field">
              <dt className="plan-identity-label">Effective through</dt>
              <dd className="plan-identity-readonly-value">
                {formatDate(form.effectiveTo)}
              </dd>
            </div>
          </dl>
        ) : (
          // ── Edit mode ────────────────────────────────────────────────────
          <div className="plan-identity-grid">
            {/* Plan name */}
            <div className="plan-identity-field plan-identity-field--full">
              <label className="plan-identity-label" htmlFor="plan-identity-plan-name">
                Plan name{' '}
                <span className="plan-identity-required">(required)</span>
              </label>
              <input
                id="plan-identity-plan-name"
                className="plan-identity-input"
                type="text"
                value={form.planName}
                onChange={handleTextChange('planName')}
                maxLength={120}
                required
                placeholder="e.g. AE Mid-Market FY27 Q1"
                aria-invalid={Boolean(fieldError('planName'))}
                aria-describedby={describedBy('planName', showPlanNameCounter ? 'plan-identity-plan-name-counter' : null)}
              />
              <div className="plan-identity-field-footer">
                {fieldError('planName') ? (
                  <p id={errorId('planName')} className="plan-identity-error" role="alert">
                    {fieldError('planName')}
                  </p>
                ) : (
                  <span aria-hidden="true" />
                )}
                {showPlanNameCounter && (
                  <p id="plan-identity-plan-name-counter" className="plan-identity-counter" aria-live="polite">
                    {planNameLength}/120
                  </p>
                )}
              </div>
            </div>

            {/* Role */}
            <div className="plan-identity-field">
              <label className="plan-identity-label" htmlFor="plan-identity-role">
                Role{' '}
                <span className="plan-identity-required">(required)</span>
              </label>
              <select
                id="plan-identity-role"
                className="plan-identity-select"
                value={form.roleId ?? ''}
                onChange={handleNumericSelectChange('roleId')}
                required
                aria-invalid={Boolean(fieldError('roleId'))}
                aria-describedby={describedBy('roleId')}
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.roleId} value={role.roleId}>
                    {role.roleName} ({role.roleCode})
                  </option>
                ))}
              </select>
              {fieldError('roleId') && (
                <p id={errorId('roleId')} className="plan-identity-error" role="alert">
                  {fieldError('roleId')}
                </p>
              )}
            </div>

            {/* Segment */}
            <div className="plan-identity-field">
              <label className="plan-identity-label" htmlFor="plan-identity-segment">
                Segment{' '}
                <span className="plan-identity-required">(required)</span>
              </label>
              <select
                id="plan-identity-segment"
                className="plan-identity-select"
                value={form.segmentId ?? ''}
                onChange={handleNumericSelectChange('segmentId')}
                required
                aria-invalid={Boolean(fieldError('segmentId'))}
                aria-describedby={describedBy('segmentId')}
              >
                <option value="">Select a segment</option>
                {segments.map((segment) => (
                  <option key={segment.segmentId} value={segment.segmentId}>
                    {segment.segmentName}
                  </option>
                ))}
              </select>
              {fieldError('segmentId') && (
                <p id={errorId('segmentId')} className="plan-identity-error" role="alert">
                  {fieldError('segmentId')}
                </p>
              )}
            </div>

            {/* Business Unit */}
            <div className="plan-identity-field plan-identity-field--full">
              <label className="plan-identity-label" htmlFor="plan-identity-business-unit">
                Business unit{' '}
                <span className="plan-identity-required">(optional)</span>
              </label>
              {readOnly ? (
                <p className="plan-identity-input" style={{ margin: 0 }}>
                  {displayValue(buName)}
                </p>
              ) : (
                <select
                  id="plan-identity-business-unit"
                  className="plan-identity-select"
                  value={form.businessUnitId ?? ''}
                  onChange={handleNumericSelectChange('businessUnitId')}
                  aria-invalid={Boolean(fieldError('businessUnitId'))}
                  aria-describedby={describedBy('businessUnitId')}
                >
                  <option value="">No business unit (applies to all)</option>
                  {businessUnits.map((bu) => (
                    <option key={bu.businessUnitId} value={bu.businessUnitId}>
                      {bu.businessUnitName}
                    </option>
                  ))}
                </select>
              )}
              {fieldError('businessUnitId') && (
                <p id={errorId('businessUnitId')} className="plan-identity-error" role="alert">
                  {fieldError('businessUnitId')}
                </p>
              )}
            </div>

            {/* Fiscal period */}
            <div className="plan-identity-field plan-identity-field--full">
              <label className="plan-identity-label" htmlFor="plan-identity-fiscal-period">
                Fiscal period{' '}
                <span className="plan-identity-required">(required)</span>
              </label>
              <select
                id="plan-identity-fiscal-period"
                className="plan-identity-select"
                value={form.fiscalPeriodId ?? ''}
                onChange={handleNumericSelectChange('fiscalPeriodId')}
                required
                aria-invalid={Boolean(fieldError('fiscalPeriodId'))}
                aria-describedby={describedBy('fiscalPeriodId')}
              >
                <option value="">Select a fiscal period</option>
                {fiscalPeriods.map((period) => (
                  <option key={period.fiscalPeriodId} value={period.fiscalPeriodId}>
                    {period.periodLabel}
                  </option>
                ))}
              </select>
              {fieldError('fiscalPeriodId') && (
                <p id={errorId('fiscalPeriodId')} className="plan-identity-error" role="alert">
                  {fieldError('fiscalPeriodId')}
                </p>
              )}
            </div>

            {/* Effective from */}
            <div className="plan-identity-field">
              <label className="plan-identity-label" htmlFor="plan-identity-effective-from">
                Effective from{' '}
                <span className="plan-identity-required">(required)</span>
              </label>
              <input
                id="plan-identity-effective-from"
                className="plan-identity-input"
                type="date"
                value={form.effectiveFrom ?? ''}
                onChange={handleTextChange('effectiveFrom')}
                required
                aria-invalid={Boolean(fieldError('effectiveFrom'))}
                aria-describedby={describedBy('effectiveFrom')}
              />
              {fieldError('effectiveFrom') && (
                <p id={errorId('effectiveFrom')} className="plan-identity-error" role="alert">
                  {fieldError('effectiveFrom')}
                </p>
              )}
            </div>

            {/* Effective to */}
            {/* S822 OI-S821-EFFECTIVE-THROUGH-LABEL: label changed (required)→(optional) per D1.
                required attribute removed — effectiveTo is always optional (D1 locked). */}
            <div className="plan-identity-field">
              <label className="plan-identity-label" htmlFor="plan-identity-effective-to">
                Effective through{' '}
                <span className="plan-identity-required">(optional)</span>
              </label>
              <input
                id="plan-identity-effective-to"
                className="plan-identity-input"
                type="date"
                value={form.effectiveTo ?? ''}
                onChange={handleTextChange('effectiveTo')}
                min={form.effectiveFrom || undefined}
                aria-invalid={Boolean(fieldError('effectiveTo'))}
                aria-describedby={describedBy('effectiveTo')}
              />
              {fieldError('effectiveTo') && (
                <p id={errorId('effectiveTo')} className="plan-identity-error" role="alert">
                  {fieldError('effectiveTo')}
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
});

PlanIdentitySection.displayName = 'PlanIdentitySection';

PlanIdentitySection.propTypes = {
  plan: PropTypes.shape({
    planId:              PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    planCode:            PropTypes.string,
    planName:            PropTypes.string,
    roleId:              PropTypes.number,
    segmentId:           PropTypes.number,
    businessUnitId:      PropTypes.number,
    fiscalPeriodId:      PropTypes.number,
    effectiveFrom:       PropTypes.string,
    effectiveTo:         PropTypes.string,
    version:             PropTypes.number,
    planStatus:          PropTypes.string,
    temporalRelationship: PropTypes.string,
  }).isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape({
    roleId:   PropTypes.number.isRequired,
    roleName: PropTypes.string.isRequired,
    roleCode: PropTypes.string.isRequired,
  })).isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({
    segmentId:   PropTypes.number.isRequired,
    segmentName: PropTypes.string.isRequired,
  })).isRequired,
  businessUnits: PropTypes.arrayOf(PropTypes.shape({
    businessUnitId:   PropTypes.number.isRequired,
    businessUnitName: PropTypes.string.isRequired,
  })).isRequired,
  fiscalPeriods: PropTypes.arrayOf(PropTypes.shape({
    fiscalPeriodId: PropTypes.number.isRequired,
    periodLabel:    PropTypes.string.isRequired,
    periodStart:    PropTypes.string.isRequired,
    periodEnd:      PropTypes.string.isRequired,
  })).isRequired,
  readOnly:         PropTypes.bool.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
};

export default PlanIdentitySection;
